import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  useStartHighlightingMutation,
  useStopHighlightingMutation,
} from "../api/apiSlice";

export default function Highlight() {
  const meetingId = useSelector((state) => state.meeting.meetingId);
  const [highlightRunning, setHighlightRunning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(60);
  
  const highlightTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  const [startHighlighting] = useStartHighlightingMutation();
  const [stopHighlighting] = useStopHighlightingMutation();

  const getCurrentTimeHHMM = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const startHighlight = async () => {
    if (!meetingId) return;
    const time = getCurrentTimeHHMM();

    try {
      await startHighlighting({ meetingId, time }).unwrap();
      setHighlightRunning(true);
      setSecondsRemaining(60);

      // Start countdown interval
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      countdownIntervalRef.current = setInterval(() => {
        setSecondsRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Auto-stop timeout
      highlightTimerRef.current = setTimeout(() => {
        stopHighlight();
      }, 60 * 1000); // auto-stop after 60 seconds
    } catch (err) {
      console.error("Failed to start highlight:", err);
    }
  };

  const stopHighlight = async () => {
    if (!meetingId) return;
    const time = getCurrentTimeHHMM();

    try {
      await stopHighlighting({ meetingId, time }).unwrap();
      setHighlightRunning(false);

      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    } catch (err) {
      console.error("Failed to stop highlight:", err);
    }
  };

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  const progressPercentage = (secondsRemaining / 60) * 100;

  return (
    <section className="widget-card">
      <h3 className="widget-title">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
        </svg>
        Meeting Highlight
      </h3>

      <div className="highlight-panel">
        <div className="highlight-state">
          <div className={`pulse-ring ${highlightRunning ? "active" : ""}`}>
            <div className="pulse-inner" />
          </div>
          <div className="highlight-meta">
            <span className="highlight-status-text">
              {highlightRunning ? "Highlight Active" : "Ready to Tag Highlight"}
            </span>
            {highlightRunning ? (
              <span className="highlight-countdown">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                Auto-stopping in {secondsRemaining}s
              </span>
            ) : (
              <span className="subtitle" style={{ margin: 0 }}>
                Tag key discussions 
              </span>
            )}
          </div>
        </div>

        <div>
          {!highlightRunning ? (
            <button
              onClick={startHighlight}
              disabled={!meetingId}
              className="btn btn-primary"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              Start Highlight
            </button>
          ) : (
            <button onClick={stopHighlight} className="btn btn-danger">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
              </svg>
              Stop Highlight
            </button>
          )}
        </div>
      </div>

      {highlightRunning && (
        <div className="countdown-container">
          <div
            className="countdown-bar"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      )}
    </section>
  );
}
